import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Shirt, Plus, Search, AlertCircle, Library, Copy, Trash2 } from "lucide-react";
import { ColourSwatch } from "@/components/ColourSwatch";
import { GarmentSizesRow } from "@/components/quote/GarmentSizesRow";
import GarmentNotes from "@/components/quote/GarmentNotes";

export const GarmentsCard = ({
  job,
  setJob,
  setHasUnsavedChanges,
  expandedSections,
  toggleSection,
  totalGarments,
  isAdmin,
  addGarment,
  quickAddSearch,
  setQuickAddSearch,
  searchQuickAdd,
  quickAddResults,
  selectQuickAddProduct,
  isSearchingQuickAdd,
  quickAddSearchDone,
  quickAddProduct,
  quickAddColour,
  setQuickAddColour,
  quickAddQty,
  setQuickAddQty,
  addQuickGarment,
  lookupColour,
  openProductPicker,
  updateGarment,
  updateGarmentFields,
  searchAutocomplete,
  closeAutocomplete,
  autocompleteGarmentId,
  autocompleteResults,
  selectAutocompleteProduct,
  autocompleteSearchDone,
  isSearchingAutocomplete,
  duplicateGarment,
  removeGarment,
}) => {
  return (
            <Collapsible 
              open={expandedSections.garments} 
              onOpenChange={() => toggleSection('garments')}
            >
            <div className="bg-white rounded-lg border border-gray-100 shadow-sm" data-testid="garments-card">
              <CollapsibleTrigger asChild>
              <div className="px-5 py-4 cursor-pointer hover:bg-gray-50/50 transition-colors rounded-t-lg">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    {expandedSections.garments ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <Shirt className="w-4 h-4 text-blue-500" />
                    Garments
                    {totalGarments > 0 && (
                      <span className="text-xs text-gray-500 font-normal ml-1">({totalGarments} total)</span>
                    )}
                  </h3>
                  {/* Add Garment button - Admin only (creates blank manual entry) */}
                  {isAdmin && (
                    <div className="flex gap-2">
                      <Button
                        onClick={(e) => { e.stopPropagation(); addGarment(); }}
                        size="sm"
                        data-testid="add-garment-btn"
                        className="h-8 text-xs bg-blue-500 hover:bg-blue-600"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                      <Button
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          const newGarment = {
                            id: crypto.randomUUID(),
                            name: "BYO Garment",
                            colour: "",
                            cost: 0,
                            quantity: 1,
                            product_id: "byo-garment-system",
                            is_byo: true,
                            handling_fee: 0,
                          };
                          setJob({ ...job, garments: [...job.garments, newGarment] });
                          setHasUnsavedChanges(true);
                        }}
                        size="sm"
                        variant="outline"
                        data-testid="add-byo-btn"
                        className="h-8 text-xs text-purple-600 border-purple-200 hover:bg-purple-50"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        BYO
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
              <div className="px-5 pb-5 border-t border-gray-100">
                {/* Quick Add Garment Row - responsive layout */}
                <div className="mt-4 mb-4 p-3 bg-blue-50/50 rounded-lg" data-testid="quick-add-garment">
                  <div className="grid grid-cols-12 gap-2 sm:gap-3 items-end">
                    {/* Search - full width on mobile, 5 cols on desktop */}
                    <div className="col-span-12 sm:col-span-5 space-y-1">
                      <Label className="text-xs font-medium text-blue-700">Quick Add</Label>
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Search garment..."
                          value={quickAddSearch}
                          onChange={(e) => {
                            setQuickAddSearch(e.target.value);
                            searchQuickAdd(e.target.value);
                          }}
                          onFocus={() => {
                            if (quickAddSearch?.length >= 2) {
                              searchQuickAdd(quickAddSearch);
                            }
                          }}
                          className="pl-9 text-sm border-gray-200"
                          data-testid="quick-add-search"
                        />
                        {/* Search Results Dropdown */}
                        {quickAddResults.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                            {quickAddResults.map((product) => (
                              <button
                                key={product.id}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 flex items-center justify-between"
                                onClick={() => selectQuickAddProduct(product)}
                                data-testid={`quick-add-result-${product.id}`}
                              >
                                <span className="font-medium truncate">
                                  {product.brand} {product.style_code} {product.product_name}
                                </span>
                                {isAdmin && product.default_cost > 0 && (
                                  <span className="text-xs text-muted-foreground ml-2 shrink-0">
                                    ${product.default_cost.toFixed(2)}
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                        {/* Staff: No results message when searching */}
                        {!isAdmin && quickAddResults.length === 0 && quickAddSearchDone && !isSearchingQuickAdd && quickAddSearch.length >= 2 && (
                          <div className="absolute z-50 w-full mt-1 bg-amber-50 border border-amber-200 rounded-md shadow-lg p-3" data-testid="quick-add-no-results">
                            <div className="flex items-start gap-2 text-amber-700">
                              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                              <div>
                                <p className="text-sm font-medium">Garment not found</p>
                                <p className="text-xs mt-1">Please ask an Admin to add this garment to the Product Library.</p>
                              </div>
                            </div>
                          </div>
                        )}
                        {isSearchingQuickAdd && (
                          <div className="absolute right-2.5 top-2.5">
                            <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Colour - 6 cols on mobile, 3 cols on desktop */}
                    <div className="col-span-6 sm:col-span-3 space-y-1">
                      <Label className="text-xs text-muted-foreground">Colour</Label>
                      {quickAddProduct?.available_colours?.length > 0 ? (
                        <Select 
                          value={quickAddColour} 
                          onValueChange={setQuickAddColour}
                        >
                          <SelectTrigger data-testid="quick-add-colour-select" className="text-sm">
                            <SelectValue placeholder="Select">
                              {quickAddColour && (
                                <span className="flex items-center gap-1.5">
                                  <ColourSwatch 
                                    hex={lookupColour(quickAddColour, quickAddProduct.supplier)?.hex} 
                                    size="xs" 
                                  />
                                  {quickAddColour}
                                </span>
                              )}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {quickAddProduct.available_colours.map((c) => {
                              const colourData = lookupColour(c, quickAddProduct.supplier);
                              return (
                                <SelectItem key={c} value={c}>
                                  <span className="flex items-center gap-1.5">
                                    <ColourSwatch hex={colourData?.hex} size="xs" />
                                    {c}
                                  </span>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          placeholder="Colour"
                          value={quickAddColour}
                          onChange={(e) => setQuickAddColour(e.target.value)}
                          data-testid="quick-add-colour-input"
                          className="text-sm"
                        />
                      )}
                    </div>
                    {/* Qty - 3 cols on mobile, 2 cols on desktop */}
                    <div className="col-span-3 sm:col-span-2 space-y-1">
                      <Label className="text-xs text-muted-foreground">Qty</Label>
                      <Input
                        type="number"
                        min="1"
                        placeholder="0"
                        value={quickAddQty}
                        onChange={(e) => setQuickAddQty(e.target.value)}
                        data-testid="quick-add-qty"
                        className="text-sm"
                      />
                    </div>
                    {/* Add button - 3 cols on mobile, 2 cols on desktop */}
                    <div className="col-span-3 sm:col-span-2">
                      <Button
                        onClick={addQuickGarment}
                        className="w-full"
                        size="sm"
                        disabled={!quickAddProduct || !quickAddQty}
                        data-testid="quick-add-btn"
                      >
                        <Plus className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">Add</span>
                      </Button>
                    </div>
                  </div>
                </div>

                {job.garments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Shirt className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No garments added yet</p>
                    <p className="text-xs">
                      {isAdmin 
                        ? 'Click "Add Garment" or use Quick Add above'
                        : 'Use the Quick Add search above to add garments from the Product Library'
                      }
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {job.garments.map((garment, index) => (
                      <div
                        key={garment.id}
                        className="grid grid-cols-2 sm:grid-cols-12 gap-2 sm:gap-3 items-end p-2 sm:p-3 bg-secondary/30 rounded-lg"
                        data-testid={`garment-row-${index}`}
                      >
                        {/* Garment Name - full width on mobile, 4 cols on desktop */}
                        <div className="col-span-2 sm:col-span-4 space-y-1">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Label className="text-xs text-muted-foreground">
                                Garment Name
                              </Label>
                              {/* BYO Badge */}
                              {(garment.is_byo || garment.product_id === "byo-garment-system") && (
                                <Badge 
                                  variant="outline" 
                                  className="text-[10px] px-1 py-0 bg-purple-100 text-purple-700 border-purple-300"
                                  data-testid={`byo-badge-${index}`}
                                >
                                  BYO
                                </Badge>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 px-1 text-xs text-indigo-600 hover:text-indigo-700"
                              onClick={() => openProductPicker(garment.id)}
                              data-testid={`select-product-${index}`}
                            >
                              <Library className="w-3 h-3 mr-1" />
                              Browse
                            </Button>
                          </div>
                          <div className="relative">
                            {isAdmin ? (
                              /* Admin: Full edit capability */
                              <Input
                                placeholder="Type to search products..."
                                value={garment.name}
                                onChange={(e) => {
                                  updateGarment(garment.id, "name", e.target.value);
                                  searchAutocomplete(garment.id, e.target.value);
                                }}
                                onFocus={() => {
                                  if (garment.name?.length >= 2) {
                                    searchAutocomplete(garment.id, garment.name);
                                  }
                                }}
                                onBlur={closeAutocomplete}
                                autoComplete="off"
                                data-testid={`garment-name-${index}`}
                              />
                            ) : (
                              /* Staff: Search trigger but name locked to product selection */
                              <>
                                <Input
                                  placeholder="Search product library..."
                                  value={garment.name}
                                  onChange={(e) => {
                                    // Staff can search but name updates only via product selection
                                    // Update the name temporarily to show search text
                                    updateGarment(garment.id, "name", e.target.value);
                                    searchAutocomplete(garment.id, e.target.value);
                                  }}
                                  onFocus={() => {
                                    if (garment.name?.length >= 2) {
                                      searchAutocomplete(garment.id, garment.name);
                                    }
                                  }}
                                  onBlur={closeAutocomplete}
                                  autoComplete="off"
                                  data-testid={`garment-name-${index}`}
                                  className={!garment.product_id ? "border-amber-300 bg-amber-50/50" : ""}
                                />
                                {/* Staff: Warning if garment not from product library */}
                                {!garment.product_id && garment.name && (
                                  <div className="mt-1 flex items-center gap-1 text-xs text-amber-600">
                                    <AlertCircle className="w-3 h-3" />
                                    <span>Please select from Product Library</span>
                                  </div>
                                )}
                              </>
                            )}
                            {/* Autocomplete dropdown */}
                            {autocompleteGarmentId === garment.id && autocompleteResults.length > 0 && (
                              <div 
                                className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
                                data-testid={`autocomplete-dropdown-${index}`}
                              >
                                {autocompleteResults.map((product) => (
                                  <div
                                    key={product.id}
                                    className="px-3 py-2 hover:bg-indigo-50 cursor-pointer border-b last:border-b-0 transition-colors"
                                    onMouseDown={() => selectAutocompleteProduct(garment.id, product)}
                                    data-testid={`autocomplete-item-${product.id}`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <Badge variant="outline" className="text-xs font-mono shrink-0">
                                          {product.style_code || "—"}
                                        </Badge>
                                        <span className="text-sm font-medium truncate">
                                          {product.brand} {product.product_name}
                                        </span>
                                      </div>
                                      {isAdmin && (
                                        <span className="font-mono text-sm text-green-700 ml-2 shrink-0">
                                          ${parseFloat(product.default_cost || 0).toFixed(2)}
                                        </span>
                                      )}
                                    </div>
                                    {product.available_colours?.length > 0 && (
                                      <div className="flex gap-1 mt-1 flex-wrap">
                                        {product.available_colours.slice(0, 4).map((c) => {
                                          const colourData = lookupColour(c, product.supplier);
                                          return (
                                            <span key={c} className="text-xs text-muted-foreground flex items-center gap-1">
                                              <ColourSwatch hex={colourData?.hex} size="xs" />
                                              {c}
                                            </span>
                                          );
                                        })}
                                        {product.available_colours.length > 4 && (
                                          <span className="text-xs text-muted-foreground">
                                            +{product.available_colours.length - 4} more
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* Staff: No results message when searching */}
                            {!isAdmin && autocompleteGarmentId === garment.id && autocompleteResults.length === 0 && autocompleteSearchDone && !isSearchingAutocomplete && (
                              <div 
                                className="absolute z-50 w-full mt-1 bg-amber-50 border border-amber-200 rounded-lg shadow-lg p-3"
                                data-testid={`no-results-message-${index}`}
                              >
                                <div className="flex items-start gap-2 text-amber-700">
                                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                  <div>
                                    <p className="text-sm font-medium">Garment not found in Product Library</p>
                                    <p className="text-xs mt-1">Please ask an Admin to add this garment to the Product Library.</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Colour - 1 col on mobile, 2 cols on desktop */}
                        <div className="col-span-1 sm:col-span-2 space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            Colour
                          </Label>
                          {garment._availableColours?.length > 0 ? (
                            <Select
                              value={garment.colour || ""}
                              onValueChange={(v) => updateGarment(garment.id, "colour", v)}
                            >
                              <SelectTrigger data-testid={`garment-colour-${index}`} className="text-sm">
                                <SelectValue placeholder="Select">
                                  {garment.colour && (
                                    <span className="flex items-center gap-1.5">
                                      <ColourSwatch 
                                        hex={lookupColour(garment.colour, garment._supplier)?.hex} 
                                        size="xs" 
                                      />
                                      {garment.colour}
                                    </span>
                                  )}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {garment._availableColours.map((c) => {
                                  const colourData = lookupColour(c, garment._supplier);
                                  return (
                                    <SelectItem key={c} value={c}>
                                      <span className="flex items-center gap-1.5">
                                        <ColourSwatch hex={colourData?.hex} size="xs" />
                                        {c}
                                      </span>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              placeholder="Colour"
                              value={garment.colour || ""}
                              onChange={(e) =>
                                updateGarment(garment.id, "colour", e.target.value)
                              }
                              data-testid={`garment-colour-${index}`}
                              className="text-sm"
                            />
                          )}
                        </div>
                        {/* Unit Cost - Admin only, hidden on mobile, 2 cols on desktop */}
                        {isAdmin && (
                          <div className="hidden sm:block sm:col-span-2 space-y-1">
                            <Label className="text-xs text-muted-foreground">
                              Unit Cost
                            </Label>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={garment.cost || ""}
                              onChange={(e) =>
                                updateGarment(
                                  garment.id,
                                  "cost",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              data-testid={`garment-cost-${index}`}
                              className="font-mono text-sm"
                            />
                          </div>
                        )}
                        {/* Handling Fee - BYO items only */}
                        {isAdmin && (garment.is_byo || garment.product_id === "byo-garment-system") && (
                          <div className="hidden sm:block sm:col-span-1 space-y-1">
                            <Label className="text-xs text-purple-600">
                              Fee
                            </Label>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={garment.handling_fee || ""}
                              onChange={(e) =>
                                updateGarment(
                                  garment.id,
                                  "handling_fee",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              data-testid={`garment-handling-fee-${index}`}
                              className="font-mono text-sm text-purple-600"
                              title="Per-garment handling fee for BYO items"
                            />
                          </div>
                        )}
                        {/* Qty (line total — overridden by size breakdown sum when sizes are entered) */}
                        <div className={`col-span-1 ${isAdmin ? 'sm:col-span-2' : 'sm:col-span-5'} space-y-1`}>
                          <Label className="text-xs text-muted-foreground">Qty</Label>
                          <Input
                            type="number"
                            min="0"
                            placeholder="0"
                            value={garment.quantity || ""}
                            onChange={(e) => updateGarment(garment.id, "quantity", parseInt(e.target.value) || 0)}
                            className="font-mono text-sm"
                            data-testid={`garment-qty-${index}`}
                          />
                        </div>
                        {/* Admin: Actions (Duplicate + Delete) */}
                        {isAdmin && (
                          <div className="col-span-1 sm:col-span-2 flex items-end justify-end gap-1">
                            {/* Duplicate Button */}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => duplicateGarment(garment.id)}
                              data-testid={`duplicate-garment-${index}`}
                              className="text-muted-foreground hover:text-foreground h-7 w-7"
                              title="Duplicate garment"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </Button>
                            {/* Delete Button */}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeGarment(garment.id)}
                              data-testid={`remove-garment-${index}`}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 w-7"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                        {/* Staff: Just Delete button */}
                        {!isAdmin && (
                          <div className="col-span-1 sm:col-span-1 flex justify-end">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeGarment(garment.id)}
                              data-testid={`remove-garment-${index}`}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        
                        {/* Garment Sizes (per-size qty; sum overrides line qty) — only for product-backed non-BYO lines */}
                        {!(garment.is_byo || garment.product_id === "byo-garment-system") && (garment._availableSizes?.length > 0) && (
                          <GarmentSizesRow
                            garment={garment}
                            sizes={garment._availableSizes || []}
                            index={index}
                            onUpdateFields={(fields) => {
                              setHasUnsavedChanges(true);
                              if (updateGarmentFields) {
                                updateGarmentFields(garment.id, fields);
                              } else {
                                setJob(prev => ({
                                  ...prev,
                                  garments: prev.garments.map(g => g.id === garment.id ? { ...g, ...fields } : g),
                                }));
                              }
                            }}
                          />
                        )}

                        {/* Garment Notes - Client Note (on PDF) & Internal Note (never on PDF) */}
                        <GarmentNotes
                          garment={garment}
                          onUpdate={(field, value) => {
                            setHasUnsavedChanges(true);
                            setJob(prev => ({
                              ...prev,
                              garments: prev.garments.map(g =>
                                g.id === garment.id ? { ...g, [field]: value } : g
                              ),
                            }));
                          }}
                          isAdmin={isAdmin}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              </CollapsibleContent>
            </div>
            </Collapsible>
  );
};
